import type { Product, ProductStatus } from "@d-m-organization/hub-shared-lib";

const stage = import.meta.env.VITE_STAGE;
const rootDomain = import.meta.env.VITE_ROOT_DOMAIN;

/**
 * Build product URL from stage.
 * prod: qr.gen-pro-hub.com
 * dev:  qr.dev.gen-pro-hub.com
 */
function productUrl(subdomain: string): string {
  if (stage === "prod") {
    return `https://${subdomain}.gen-pro-hub.com`;
  }
  return `https://${subdomain}.${rootDomain}`;
}

// ── Brand color mapping per product ─────────────────────────────────────────

export interface ProductColors {
  accent: string;
  accentLight: string;
}

export const PRODUCT_COLORS: Record<string, ProductColors> = {
  qr:          { accent: "#349DFF", accentLight: "#E5F2FF" },
  invoice:     { accent: "#FF861D", accentLight: "#FFF0E2" },
  planner:     { accent: "#351DBD", accentLight: "#EDE9FC" },
  "link-page": { accent: "#349DFF", accentLight: "#E5F2FF" },
  menu:        { accent: "#FF861D", accentLight: "#FFF0E2" },
  events:      { accent: "#351DBD", accentLight: "#EDE9FC" },
};

export type ProductIconType = "qr" | "file" | "calendar" | "link" | "menu" | "event";

/**
 * Hub-specific product type that extends the shared Product interface
 * with icon and color information for the dashboard/landing UI.
 */
export interface HubProduct extends Product {
  icon: ProductIconType;
  colors: ProductColors;
}

export const PRODUCTS: HubProduct[] = [
  {
    id: "qr",
    name: "QR Generator",
    description: "Create & manage dynamic QR codes with analytics and custom designs",
    url: productUrl("qr"),
    status: "live",
    icon: "qr",
    colors: PRODUCT_COLORS["qr"],
  },
  {
    id: "invoice",
    name: "Invoice",
    description: "Professional invoices with QR payment links and automatic reminders",
    url: productUrl("invoice"),
    status: "coming-soon",
    icon: "file",
    colors: PRODUCT_COLORS["invoice"],
  },
  {
    id: "planner",
    name: "Planner",
    description: "Organize events and projects with custom workflows",
    url: productUrl("planner"),
    // Single-product launch: QR ships solo first. Flip back to "live" to
    // reveal the ecosystem UI automatically (see deriveProductMode below).
    status: "coming-soon",
    icon: "calendar",
    colors: PRODUCT_COLORS["planner"],
  },
  {
    id: "link-page",
    name: "Link Page",
    description: "Digital business card and link-in-bio for your brand",
    url: productUrl("link"),
    status: "coming-soon",
    icon: "link",
    colors: PRODUCT_COLORS["link-page"],
  },
  {
    id: "menu",
    name: "Digital Menu",
    description: "QR-powered menus for restaurants and cafes",
    url: productUrl("menu"),
    status: "coming-soon",
    icon: "menu",
    colors: PRODUCT_COLORS["menu"],
  },
  {
    id: "events",
    name: "Event Page",
    description: "Public event pages with RSVP and ticket management",
    url: productUrl("events"),
    status: "coming-soon",
    icon: "event",
    colors: PRODUCT_COLORS["events"],
  },
];

// ── Single-product presentation mode ────────────────────────────────────────
//
// The hub launches one product at a time. While only one product is live, the
// dashboard hides the ecosystem chrome (app switcher, "Your apps" rail of
// coming-soon items, per-product progress bar, coming-soon showcase). Flip a
// second product to `status: "live"` and the ecosystem UI returns automatically
// — this is derived purely from the registry, nothing hardcodes "qr".
//
// See docs/design/single-product-mode-DESIGN.md.

/** A product a user can actually open (live or beta). */
export function isClickableStatus(status: ProductStatus): boolean {
  return status === "live" || status === "beta";
}

export interface ProductMode {
  liveProducts: HubProduct[];
  isSingleProductMode: boolean;
  soleProduct: HubProduct | null;
}

/** Pure derivation — unit-tested against synthetic registries. */
export function deriveProductMode(products: HubProduct[]): ProductMode {
  const liveProducts = products.filter((p) => isClickableStatus(p.status));
  const isSingleProductMode = liveProducts.length <= 1;
  return {
    liveProducts,
    isSingleProductMode,
    soleProduct: isSingleProductMode ? liveProducts[0] ?? null : null,
  };
}

const MODE = deriveProductMode(PRODUCTS);

/** Products the user can open right now. */
export const LIVE_PRODUCTS = MODE.liveProducts;
/** True while the hub is presenting a single product. */
export const IS_SINGLE_PRODUCT_MODE = MODE.isSingleProductMode;
/** The sole live product in single-product mode, else null. */
export const SOLE_PRODUCT = MODE.soleProduct;
